/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { Injectable } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Observable, of } from 'rxjs';
import { ContractRequestRule, ContractRequestRuleTypes } from 'src/app/shared/lib/contract-request/models';
import { MarginProperties } from 'src/app/shared/models/margin-properties.model';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { BaseBillingRate } from '../models';
import { ContractLengthWithBookingWeeksResponse } from '../models/contract-length-with-booking-weeks-response.model';
import { Job } from '../models/job.model';
import { BillingRate } from '../models/pay-billing.model';
import { MarginCalculatorDateService } from './margin-calculator-date.service';
import { MarginCalculatorFormService } from './margin-calculator-form.service';
import { MarginCalculatorFormulaService } from './margin-calculator-formula.service';
import { MarginCalculatorBillingService } from './margin-calculator-pay-billing.service';

@UntilDestroy()
@Injectable({
    providedIn: 'root'
})
export class MarginCalculatorContractInfoHelperService {
    constructor(
        private readonly _marginBillingService: MarginCalculatorBillingService,
        private readonly _marginDateService: MarginCalculatorDateService,
        private readonly _marginFormulaService: MarginCalculatorFormulaService,
        private readonly _marginFormService: MarginCalculatorFormService,
        private readonly _toasterService: ToasterService
    ) {}

    static addSingleRuleType(rule: ContractRequestRule, singleRules: Set<number>): void {
        switch (rule.contractRequestRuleTypeId) {
            case ContractRequestRuleTypes.CompletedContract:
            case ContractRequestRuleTypes.AdditionalDocuments:
                singleRules.add(rule.contractRequestRuleTypeId);
        }
    }

    static hasChangedFields(
        currentMargin: MarginProperties,
        newMargin: MarginProperties,
        rule: ContractRequestRule
    ): boolean {
        if (newMargin && currentMargin && rule?.changeFields) {
            for (const field of rule.changeFields.split(',')) {
                if (currentMargin[field] !== newMargin[field]) {
                    return true;
                }
            }
        }
        return false;
    }

    static ruleChecker(
        ruleList: ContractRequestRule[],
        currentMargin: MarginProperties,
        newMargin: MarginProperties,
        ayaPaidBonusCoCApprovalEnabled: boolean
    ): ContractRequestRule[] {
        const result = [];

        /** Has the list of rule types which should be used only once */
        const singleRules = new Set<number>();
        const ruleNames = new Set<string>();

        for (const rule of ruleList) {
            if (singleRules.has(rule.contractRequestRuleTypeId)) {
                continue;
            }
            this.addSingleRuleType(rule, singleRules);
            if (ruleNames.has(rule.ruleName)) {
                continue;
            }

            // The logic for pink stipends has been moved to the backend
            // If the feature flag is on we want to skip hasChangedFields for pink stipends
            if (rule.ruleName === 'Pink Stipends') {
                ruleNames.add(rule.ruleName);
                result.push(rule);
                continue;
            }

            if (
                ayaPaidBonusCoCApprovalEnabled &&
                rule.contractRequestRuleTypeId === Number(ContractRequestRuleTypes.AyaPaidBonus)
            ) {
                ruleNames.add(rule.ruleName);
                result.push(rule);
                continue;
            }

            if (newMargin && rule.changeFields && !this.hasChangedFields(currentMargin, newMargin, rule)) {
                continue;
            }

            ruleNames.add(rule.ruleName);
            result.push(rule);
        }
        return result;
    }

    populateTimeFrame(
        shiftTime: number,
        shiftHours: number
    ): { startHours: number; startMinutes: number; endHours: number; endMinutes: number } {
        const timeFrame = this._marginDateService.getTimeFrame(this.getShiftTimeString(shiftTime));

        const startTimeFrameHours = timeFrame.startHours;
        const startTimeFrameMinutes = timeFrame.startMinutes;
        let endTimeFrameHours = this._marginDateService.getEndHours(startTimeFrameHours, shiftHours);
        let endTimeFrameMinutes = this._marginDateService.getEndMinutes(startTimeFrameMinutes, shiftHours);

        if (endTimeFrameMinutes >= 60) {
            endTimeFrameHours += 1;
            endTimeFrameMinutes = 0;
        }

        return {
            startHours: startTimeFrameHours,
            startMinutes: startTimeFrameMinutes,
            endHours: endTimeFrameHours,
            endMinutes: endTimeFrameMinutes
        };
    }

    setBillableHours(
        shiftType: number,
        shiftHours: number,
        altShiftHours: number,
        shifts: number,
        altShifts: number,
        bookingWeeks: number,
        altBookingWeeks: number,
        startDate: Date,
        endDate: Date,
        vacationDates: Date[],
        otBillingRulesEnabled: boolean,
        otFirstHours: number,
        otSecondHours: number,
        otFirstFrequency: string,
        otSecondFrequency: string,
        otAndOr: string,
        orientationHours: number,
        billingRates?: BillingRate[]
    ): {
        billingOtHours: number;
        billingRegHours: number;
        primaryNonStandardOvertimeBillingHours: number;
        alternateNonStandardOvertimeBillingHours: number;
    } {
        const hasMultipleOtRules =
            this._marginBillingService.haveMultipleRules('overTime', shiftHours, billingRates) ||
            (otAndOr !== '-' && otAndOr !== '');

        let otWeekOrDayFirst = '';
        let otWeekOrDaySecond = '';
        let otHoursFirst = 0;
        let otHoursSecond = 0;

        bookingWeeks = bookingWeeks || 1;

        if (otBillingRulesEnabled) {
            if (hasMultipleOtRules) {
                otWeekOrDayFirst = otFirstFrequency;
                otWeekOrDaySecond = otSecondFrequency;
                otHoursFirst = otFirstHours;
                otHoursSecond = otSecondHours;
            } else {
                otWeekOrDayFirst = otFirstFrequency;
                otHoursFirst = otFirstHours;
            }
        }

        const otFirstHourCount = this._marginBillingService.getHourCount('overTime', 1, shiftHours, billingRates);
        const otSecondHourCount = this._marginBillingService.getHourCount('overTime', 2, shiftHours, billingRates);
        let primaryNonStandardOvertimeBillingHours = 0;
        let alternateNonStandardOvertimeBillingHours = 0;
        if (shiftType > 0) {
            const billingOtHours = this._marginFormulaService.calculateNonStandardOverTimeBillingHours(
                shifts,
                shiftHours,
                shiftType === 1 ? altShifts : shifts,
                shiftType === 1 ? altShiftHours : shiftHours,
                startDate,
                endDate,
                vacationDates,
                otWeekOrDayFirst,
                otAndOr !== '' ? otAndOr : '-',
                otHoursFirst,
                otWeekOrDaySecond,
                otHoursSecond,
                otFirstHourCount,
                otSecondHourCount
            );
            primaryNonStandardOvertimeBillingHours = this._marginBillingService.primaryNonStandardOvertimeBillingHours;
            alternateNonStandardOvertimeBillingHours =
                this._marginBillingService.alternateNonStandardOvertimeBillingHours;
            const billingRegHours = this._marginFormulaService.calculateNonStandardBillingHours(
                shifts,
                shiftHours,
                shiftType === 1 ? altShifts : shifts,
                shiftType === 1 ? altShiftHours : shiftHours,
                startDate,
                endDate,
                vacationDates,
                0,
                primaryNonStandardOvertimeBillingHours ?? 0,
                alternateNonStandardOvertimeBillingHours ?? 0
            );

            return {
                billingRegHours,
                billingOtHours,
                primaryNonStandardOvertimeBillingHours,
                alternateNonStandardOvertimeBillingHours
            };
        }

        const billingOtHours = this._marginFormulaService.calculateOverTimeBillingHour(
            otWeekOrDayFirst,
            otAndOr !== '' ? otAndOr : '-',
            otHoursFirst,
            otWeekOrDaySecond,
            otHoursSecond,
            shiftHours,
            shifts,
            bookingWeeks,
            otFirstHourCount,
            otSecondHourCount
        );

        const billingRegHours = this._marginFormulaService.calculateRegularBillingHour(
            shiftHours,
            shifts,
            bookingWeeks,
            0,
            billingOtHours
        );

        return {
            billingRegHours,
            billingOtHours,
            primaryNonStandardOvertimeBillingHours,
            alternateNonStandardOvertimeBillingHours
        };
    }

    setBaseBillingRate(
        isAlternate: boolean,
        shiftHours: number,
        shiftTime: number,
        existingRate: number,
        job?: Job,
        billingRates?: BillingRate[],
        marginExists?: boolean,
        isInitialSpecialtyChanged?: boolean
    ): BaseBillingRate {
        const rate = MarginCalculatorBillingService.findBaseBillRate(
            this.getShiftTimeString(shiftTime),
            shiftHours,
            billingRates
        );

        if (!isAlternate && !isInitialSpecialtyChanged && job?.jobId && !marginExists) {
            return {
                value: job.billingRate,
                originalBillingRate: rate,
                jobBillingRate: job?.billingRate ? job.billingRate : 0,
                resetValidation: true
            } as BaseBillingRate;
        }

        if ((!rate || rate === 0) && this._marginFormService.parentForm.dirty) {
            this._toasterService.toast('No billing rate for this specialty at this facility');
        }

        if (isAlternate) {
            return {
                value:
                    existingRate && !this._marginFormService.parentForm.dirty && !job?.jobId
                        ? existingRate
                        : job?.jobId
                          ? job?.billingRate
                          : rate,
                originalBillingRate: rate,
                jobBillingRate: job?.billingRate ? job?.billingRate : 0,
                resetValidation: true
            } as BaseBillingRate;
        }

        return {
            value: existingRate && !this._marginFormService.parentForm.dirty ? existingRate : rate,
            originalBillingRate: rate,
            jobBillingRate: job?.billingRate ? job.billingRate : 0,
            resetValidation: true
        } as BaseBillingRate;
    }

    calculateWeeks(
        shiftType: number,
        startDate: Date,
        endDate: Date,
        vacationDates: Date[]
    ): { bookingWeeks: number; altBookingWeeks: number } {
        let bookingWeeks = 0;
        let altBookingWeeks = 0;
        if (shiftType > 0) {
            bookingWeeks = this._marginFormulaService.calculatePrimaryBookingWeeks(startDate, endDate, vacationDates);

            altBookingWeeks = this._marginFormulaService.calculateAlternatingBookingWeeks(
                startDate,
                endDate,
                vacationDates
            );
        } else {
            bookingWeeks = this._marginFormulaService.calculateStandardBookingWeeks(startDate, endDate, vacationDates);
        }

        return { bookingWeeks, altBookingWeeks };
    }

    getShiftTimeString(value: number): string {
        if (value === 1) {
            return 'day';
        } else if (value === 2) {
            return 'evening';
        } else if (value === 3) {
            return 'night';
        } else if (value === 4) {
            return 'variable';
        }
    }

    getContractLengthWithBookingWeeks(
        shiftType: number,
        startDate: Date,
        endDate: Date,
        vacationDates: Date[]
    ): Observable<ContractLengthWithBookingWeeksResponse> {
        const contractLength = this._marginFormulaService.calculateWeeks(startDate, endDate);
        const { bookingWeeks, altBookingWeeks } = this.calculateWeeks(shiftType, startDate, endDate, vacationDates);

        return of({
            bookingWeeks,
            altBookingWeeks,
            contractLength
        } as ContractLengthWithBookingWeeksResponse);
    }

    setPayHours(
        shiftType: number,
        facilityState: string,
        numOfShifts: number,
        shiftHours: number,
        bookingWeeks: number,
        altWeeks?: number,
        altNumShifts?: number,
        altShiftHours?: number
    ): { regPayHrs: number; otPayHrs: number; altRegPayHrs: number; altOtPayHrs: number } {
        if (shiftType > 0) {
            const regPayHrs = this._marginFormulaService.calculateRegularPayHours(
                facilityState,
                numOfShifts,
                shiftHours,
                bookingWeeks
            );

            const otPayHrs = this._marginFormulaService.calculateRegularOverTimePayHours(
                facilityState,
                numOfShifts,
                shiftHours,
                bookingWeeks
            );

            const altRegPayHrs = this._marginFormulaService.calculateRegularPayHours(
                facilityState,
                shiftType === 1 ? altNumShifts : numOfShifts,
                shiftType === 1 ? altShiftHours : shiftHours,
                altWeeks
            );

            const altOtPayHrs = this._marginFormulaService.calculateRegularOverTimePayHours(
                facilityState,
                shiftType === 1 ? altNumShifts : numOfShifts,
                shiftType === 1 ? altShiftHours : shiftHours,
                altWeeks
            );

            return { regPayHrs, otPayHrs, altRegPayHrs, altOtPayHrs };
        } else {
            const regPayHrs = this._marginFormulaService.calculateRegularPayHours(
                facilityState,
                numOfShifts,
                shiftHours,
                bookingWeeks
            );

            const otPayHrs = this._marginFormulaService.calculateRegularOverTimePayHours(
                facilityState,
                numOfShifts,
                shiftHours,
                bookingWeeks
            );

            return { regPayHrs, otPayHrs, altRegPayHrs: 0, altOtPayHrs: 0 };
        }
    }

    static getApiValidationError(response: any, customErrorMessage): string {
        return response?.error && response?.error.errors && response?.error.errors['Validation']
            ? response?.error.errors['Validation'][0]
            : customErrorMessage
              ? customErrorMessage
              : 'An error occurred';
    }
}
